import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { NAME_FILTERS } from 'constants/dataSets';
import buildFilterCatalogue from 'utils/buildFilterCatalogue';
import { createCountryDetailsLookup } from 'utils/country';
import { formatStringToCamelCase } from 'utils/formatText';
import {
  COUNTRY_CODES_FILTER_KEY,
  getFilterIdentifier,
  isCountryCodeFilter,
  ORG_STRUCTURE_FILTER_QUERY_KEY,
  pluralizeFilterType,
} from 'utils/valueChainHelpers';

import type IFilterData from '../interfaces/IFilterData';
import type { IUseFiltersParams } from '../interfaces/IUseFiltersParams';
import type IUseFiltersReturn from '../interfaces/IUseFiltersReturn';

const useFilters = ({
  data,
  locationsCountries,
}: IUseFiltersParams): IUseFiltersReturn => {
  const { t } = useTranslation();
  const [openFilters, setOpenFilters] = useState(false);
  const [filtersData, setFiltersData] = useState<IFilterData[]>([]);
  const [selectedCurrentFilters, setSelectedCurrentFilters] = useState<IFilterData[]>([]);
  const [requestFilters, setRequestFilters] = useState<string | undefined>(undefined);
  const countryDetailsLookup = useMemo(
    () => createCountryDetailsLookup(locationsCountries?.data),
    [locationsCountries?.data],
  );

  const onOpenFilters = useCallback(() => {
    setOpenFilters((prev) => !prev);
  }, []);

  const onApplyFilters = useCallback((filtersToApply: IFilterData[] = selectedCurrentFilters) => {
    if (filtersToApply.length === 0) {
      setRequestFilters(undefined);
      return;
    }
    const queryParams: string[] = [];
    filtersToApply.forEach((filter) => {
      const pluralType = pluralizeFilterType(filter.type);
      const filterIdentifier = getFilterIdentifier(filter);

      if (filter.type === NAME_FILTERS.REVENUE) {
        if (filter.min !== undefined && filter.max !== undefined) {
          const revenueValue = `${filter.min}:${filter.max}`;
          queryParams.push(`${filter.type}=${encodeURIComponent(revenueValue)}`);
        }
      } else if (filter.type === NAME_FILTERS.ORG_STRUCTURE) {
        if (filterIdentifier) {
          queryParams.push(`${ORG_STRUCTURE_FILTER_QUERY_KEY}=${encodeURIComponent(filterIdentifier)}`);
        }
      } else if (filterIdentifier) {
        const typeFilter = isCountryCodeFilter(filter.type)
          ? COUNTRY_CODES_FILTER_KEY
          : formatStringToCamelCase(pluralType);
        const typeBalanceSheetAsset = typeFilter === NAME_FILTERS.BALANCE_SHEET_ASSET
          ? NAME_FILTERS.ASSETS : typeFilter;
        queryParams.push(`${typeBalanceSheetAsset}=${encodeURIComponent(filterIdentifier)}`);
      }
    });
    const queryString = queryParams.join('&');
    setRequestFilters(queryString);
  }, [selectedCurrentFilters]);

  useEffect(() => {
    onApplyFilters();
  }, [selectedCurrentFilters, onApplyFilters]);

  useEffect(() => {
    if (data?.data.filters && locationsCountries?.data) {
      setFiltersData(buildFilterCatalogue({
        rawFilters: data.data.filters,
        countryDetailsLookup,
        translate: (key: string, fallback: string) => t(key, fallback),
      }));
    } else {
      setFiltersData([]);
    }
  }, [countryDetailsLookup, data?.data.filters, locationsCountries?.data, t]);

  return {
    openFilters,
    setOpenFilters,
    filtersData,
    selectedCurrentFilters,
    setSelectedCurrentFilters,
    requestFilters,
    onOpenFilters,
    onApplyFilters,
  };
};

export default useFilters;
